/**
 * What Could Change Recommendation
 * Generates scenario sensitivity bullets showing what inputs could change the recommendation
 */

import type { SystemRecInput } from './types';

/**
 * Generate "what could change this recommendation" bullets
 * Based on missing anchors and key constraints
 * Max 4 bullets, short sentences
 */
export function generateWhatCouldChange(input: SystemRecInput): string[] {
  const bullets: string[] = [];

  // Check for missing data that could change recommendation
  const hasAnnualUsage = !!input.baselineEnabled && 
    (input.currentSystemEfficiency?.sedbukSeasonal != null || 
     input.currentSystemEfficiency?.value != null);
  
  const hasHeatLoss = !!(input.heatLossKw || input.knownHeatLossKw);
  
  const considersHeatPump = input.compareOptions?.some(opt => 
    opt.heatSource?.includes('heat_pump') || 
    opt.heatSource?.includes('ashp') ||
    opt.heatSource?.includes('gshp')
  ) || false;

  const hasLowMainsPressure = input.mainsPressure && input.mainsPressure < 1.5;

  const pipeworkInScreedNotSealed = input.pipeworkInScreed === "yes" && 
                                    input.systemAlreadySealed !== "yes";

  // Build bullets (max 4)
  if (!hasAnnualUsage) {
    bullets.push("If your measured annual usage differs materially from the estimate, the running-cost ranking may change.");
  }

  if (!hasHeatLoss && bullets.length < 4) {
    bullets.push("If heat loss is higher/lower than assumed, system sizing and suitability may change.");
  }

  if (considersHeatPump && bullets.length < 4) {
    bullets.push("If the system is made low-temperature compatible, heat pump running costs improve.");
  }

  if (hasLowMainsPressure && bullets.length < 4) {
    bullets.push("If pressure is confirmed low, some hot-water configurations may be ruled out.");
  }

  if (pipeworkInScreedNotSealed && bullets.length < 4) {
    bullets.push("If pipework is buried and not already sealed, sealing may be avoided.");
  }

  // If we have no bullets (rare), return a default message
  if (bullets.length === 0) {
    bullets.push("No major changes expected from additional inputs.");
  }

  return bullets.slice(0, 4);
}
